import React, { useState } from "react";
import { Trash2 } from "lucide-react";
import { Modal, Fld, ConfirmButton } from "../ui/primitives.jsx";
import { AttachField, AttachViewer } from "../ui/attachments.jsx";
import { useToast } from "../ui/Toasts.jsx";
import { EQ_CATEGORIES } from "../lib/format.js";
import { rowToForm, useSaveEquipment, useDeleteEquipment } from "./api.js";

export default function EquipmentForm({ eq, onClose }) {
  const isNew = !eq?.id;
  const [f, setF] = useState(() => {
    const base = rowToForm(eq ?? {});
    return { ...base, category: base.category || EQ_CATEGORIES[0] };
  });
  const [err, setErr] = useState("");
  const [viewing, setViewing] = useState(null);
  const { notify } = useToast();

  const save = useSaveEquipment();
  const del = useDeleteEquipment();
  const busy = save.isPending || del.isPending;

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  async function onSave() {
    setErr("");
    if (!f.name.trim()) {
      setErr("Give the machine a name.");
      return;
    }
    try {
      await save.mutateAsync({ id: eq?.id, form: f, previous: eq });
      notify(isNew ? `${f.name.trim()} added to the fleet.` : "Changes saved.");
      onClose();
    } catch (e) {
      setErr(e.message);
    }
  }

  async function onDelete() {
    setErr("");
    try {
      await del.mutateAsync({ id: eq.id, paths: [eq.photo_path, eq.manual_path] });
      notify("Machine deleted.");
      onClose();
    } catch (e) {
      // Stay open — the message explains what to do instead.
      setErr(e.message);
    }
  }

  return (
    <Modal
      title={isNew ? "Add machine" : "Edit machine"}
      onClose={onClose}
      wide
      footer={
        <>
          {!isNew && (
            <ConfirmButton
              className="btn danger"
              disabled={busy}
              label={<><Trash2 size={15} /> Delete</>}
              confirmLabel="Click again to delete"
              onConfirm={onDelete}
            />
          )}
          <div style={{ flex: 1 }} />
          <button className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" onClick={onSave} disabled={busy}>
            {busy ? "Saving…" : isNew ? "Add to fleet" : "Save changes"}
          </button>
        </>
      }
    >
      {err && <div className="notice red">{err}</div>}

      <div className="frm">
        <Fld full label="Display name">
          <input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Kubota SSV75 Skid Steer" />
        </Fld>

        <Fld label="Category">
          <select value={f.category} onChange={(e) => set("category", e.target.value)}>
            {EQ_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </Fld>

        <Fld label="Status">
          <select value={f.status} onChange={(e) => set("status", e.target.value)}>
            <option value="available">Available</option>
            <option value="maintenance">Down for maintenance</option>
            <option value="retired">Retired / sold</option>
          </select>
        </Fld>

        <Fld label="Make"><input value={f.make} onChange={(e) => set("make", e.target.value)} /></Fld>
        <Fld label="Model"><input value={f.model} onChange={(e) => set("model", e.target.value)} /></Fld>
        <Fld label="Serial / VIN"><input value={f.serial} onChange={(e) => set("serial", e.target.value)} /></Fld>
        <Fld label="Year"><input value={f.year} onChange={(e) => set("year", e.target.value)} /></Fld>

        <Fld label="Day rate ($)">
          <input type="number" value={f.dayRate} onChange={(e) => set("dayRate", e.target.value)} />
        </Fld>
        <Fld label="Week rate ($)">
          <input type="number" value={f.weekRate} onChange={(e) => set("weekRate", e.target.value)} />
        </Fld>
        <Fld label="Month rate ($)">
          <input type="number" value={f.monthRate} onChange={(e) => set("monthRate", e.target.value)} />
        </Fld>

        <Fld label="Hour meter">
          <input value={f.hourMeter} onChange={(e) => set("hourMeter", e.target.value)} placeholder="e.g. 412.6" />
        </Fld>
        <Fld label="Next service date">
          <input type="date" value={f.nextServiceDate} onChange={(e) => set("nextServiceDate", e.target.value)} />
        </Fld>
        <Fld label="Next service at (hrs)">
          <input value={f.nextServiceHours} onChange={(e) => set("nextServiceHours", e.target.value)} placeholder="e.g. 500" />
        </Fld>

        <Fld full label="Notes">
          <textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} />
        </Fld>

        {viewing && (
          <AttachViewer path={viewing.path} name={viewing.name} onClose={() => setViewing(null)} />
        )}

        <Fld full label="Photo">
          <AttachField
            path={f.photoPath}
            folder="equipment"
            accept="image/*"
            label="Upload photo"
            onView={setViewing}
            onChange={({ path }) => set("photoPath", path)}
          />
        </Fld>

        <Fld
          full
          label="User manual"
          hint="PDF or photos of the manual, viewable from the fleet list."
        >
          <AttachField
            path={f.manualPath}
            name={f.manualName}
            folder="equipment"
            accept=".pdf,image/*"
            label="Upload manual"
            onView={setViewing}
            onChange={({ path, name }) => { set("manualPath", path); set("manualName", name); }}
          />
        </Fld>
      </div>
    </Modal>
  );
}
